import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { AddOutlined, DeleteOutline } from "@mui/icons-material";
import { alarmDemoSteps } from "../../../constants";

const FeatureCard = ({ icon, title, content }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 4,
      bgcolor: "rgba(15,23,42,0.85)",
      borderColor: "rgba(148,163,184,0.25)",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: "0 0 30px rgba(59,130,246,0.35)",
        transform: "translateY(-2px)",
      },
      transition: "all 0.2s ease-out",
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(37,99,235,0.18)",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.78)", mt: 0.5 }}>
            {content}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const AlarmDemo = () => {
  const [tankAlarms, setTankAlarms] = useState([
    {
      type: "volume",
      tank: "Regular",
      threshold: 2000,
      tankIdentifier: "t1",
    },
    {
      type: "temp",
      tank: "Diesel",
      threshold: 65,
      tankIdentifier: "t2",
    },
  ]);

  const handleAddAlarm = () => {
    setTankAlarms((prev) => [
      ...prev,
      { type: "volume", tank: "Diesel", threshold: "", tankIdentifier: "" },
    ]);
  };

  const handleRemoveAlarm = (index) => {
    setTankAlarms((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setTankAlarms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // shared dark form styling (keeps text white)
  const darkFieldSx = {
    "& .MuiInputLabel-root": { color: "rgba(226,232,240,0.75)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(148,163,184,0.35)" },
      "&:hover fieldset": { borderColor: "rgba(226,232,240,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& .MuiSvgIcon-root": { color: "rgba(226,232,240,0.85)" },
  };

  return (
    <Box component="section" id="alarms" sx={{ py: 10 }}>
      <Grid container spacing={6} alignItems="flex-start">
        {/* Left copy */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, color: "#fff", fontFamily: "Poppins, sans-serif" }}
            >
              Stay Informed with Custom Notifications
            </Typography>

            <Typography variant="h3" sx={{ color: "rgba(226,232,240,0.8)" }}>
              Configure alarms by tank and threshold. Get notified when fuel drops,
              temperature shifts, or water levels rise — so you can act before it
              becomes a problem.
            </Typography>
          </Stack>
        </Grid>

        {/* Alarm configuration demo */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={10}
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: "rgba(15,23,42,0.85)",
              border: "1px solid rgba(148,163,184,0.25)",
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: 900 }}>
                Alarm Configuration
              </Typography>

              <Stack spacing={1.5}>
                {tankAlarms.map((alarm, index) => (
                  <Grid
                    container
                    spacing={1.5}
                    alignItems="center"
                    key={`${alarm.tank}-${alarm.type}-${index}`}
                  >
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small" sx={darkFieldSx}>
                        <InputLabel>Tank</InputLabel>
                        <Select
                          value={alarm.tank}
                          label="Tank"
                          onChange={(e) => handleChange(index, "tank", e.target.value)}
                        >
                          <MenuItem value="Regular">Regular</MenuItem>
                          <MenuItem value="Premium">Premium</MenuItem>
                          <MenuItem value="Diesel">Diesel</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                      <FormControl fullWidth size="small" sx={darkFieldSx}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={alarm.type}
                          label="Type"
                          onChange={(e) => handleChange(index, "type", e.target.value)}
                        >
                          <MenuItem value="volume">Volume</MenuItem>
                          <MenuItem value="height">Height</MenuItem>
                          <MenuItem value="water_volume">Water Volume</MenuItem>
                          <MenuItem value="temp">Temperature</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        label="Threshold"
                        value={alarm.threshold}
                        onChange={(e) => handleChange(index, "threshold", e.target.value)}
                        fullWidth
                        sx={darkFieldSx}
                      />
                    </Grid>

                    <Grid item xs={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        onClick={() => handleRemoveAlarm(index)}
                        sx={{
                          color: "rgba(226,232,240,0.85)",
                          "&:hover": { color: "#fff" },
                        }}
                        aria-label="Remove alarm"
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Stack>

              <Button
                startIcon={<AddOutlined />}
                variant="contained"
                color="success"
                onClick={handleAddAlarm}
                sx={{ fontWeight: 900, borderRadius: 3 }}
              >
                Add Alarm
              </Button>

              <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.6)" }}>
                Demo UI: in production this ties to your tank list and notification channels.
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        {/* Feature bullets */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {alarmDemoSteps.map((f) => (
              <FeatureCard key={f.id} icon={f.icon} title={f.title} content={f.content} />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AlarmDemo;
